import { Graphics, Sprite, Texture, Assets, Container, Text } from 'pixi.js';

const BEACON_COLOR = 0xff4444;      // Red primary
const BEACON_ACCENT = 0xff6666;     // Lighter red
const BEACON_DIM = 0x882222;        // Dimmed red

// SVG texture cache
let beaconTexture: Texture | null = null;
let beaconTextureLoading = false;

async function loadBeaconTexture(): Promise<Texture | null> {
  if (beaconTexture) return beaconTexture;
  if (beaconTextureLoading) return null;
  beaconTextureLoading = true;
  try {
    beaconTexture = await Assets.load<Texture>('/assets/sprites/beacon.svg');
    return beaconTexture;
  } catch {
    return null;
  }
}

export class InterdictionBeacon extends Container {
  public readonly id: string;
  public readonly ownerId: string;
  public readonly ownerName: string;
  public readonly radius: number;
  public readonly expiresAt: number;

  private rangeGraphics: Graphics;
  private waveGraphics: Graphics;
  private sprite: Sprite | null = null;
  private fallbackGraphics: Graphics;
  private nameLabel: Text;

  private pulseTime: number = 0;
  private waves: Array<{ radius: number; maxRadius: number; alpha: number }> = [];
  private waveTimer: number = 0;
  private readonly WAVE_INTERVAL = 1200; // Slow pulse waves

  constructor(
    x: number,
    y: number,
    id: string,
    ownerId: string,
    ownerName: string,
    radius: number,
    expiresAt: number,
  ) {
    super();

    this.position.set(x, y);
    this.id = id;
    this.ownerId = ownerId;
    this.ownerName = ownerName;
    this.radius = radius;
    this.expiresAt = expiresAt;

    this.sortableChildren = true;

    // Range circle (faint area of effect)
    this.rangeGraphics = new Graphics();
    this.rangeGraphics.zIndex = 0;
    this.addChild(this.rangeGraphics);

    // Wave animation layer
    this.waveGraphics = new Graphics();
    this.waveGraphics.zIndex = 1;
    this.addChild(this.waveGraphics);

    // Fallback graphics (diamond shape) until SVG loads
    this.fallbackGraphics = new Graphics();
    this.fallbackGraphics.zIndex = 2;
    this.drawFallback();
    this.addChild(this.fallbackGraphics);

    // Owner name label
    this.nameLabel = new Text({
      text: `${ownerName}'s Beacon`,
      style: {
        fontFamily: 'monospace',
        fontSize: 10,
        fill: BEACON_COLOR,
        align: 'center',
      },
    });
    this.nameLabel.anchor.set(0.5, 0);
    this.nameLabel.position.set(0, 18);
    this.nameLabel.zIndex = 3;
    this.addChild(this.nameLabel);

    // Load SVG async
    this.loadSprite();

    // Spawn initial burst wave
    this.waves.push({ radius: 10, maxRadius: this.radius, alpha: 0.8 });
  }

  private async loadSprite(): Promise<void> {
    const texture = await loadBeaconTexture();
    if (texture && !this.destroyed) {
      this.sprite = new Sprite(texture);
      this.sprite.anchor.set(0.5, 0.5);
      this.sprite.width = 28;
      this.sprite.height = 28;
      this.sprite.tint = BEACON_COLOR;
      this.sprite.zIndex = 2;
      this.addChild(this.sprite);
      // Hide fallback
      this.fallbackGraphics.visible = false;
    }
  }

  private drawFallback(): void {
    const g = this.fallbackGraphics;
    g.clear();
    // Diamond shape
    g.moveTo(0, -10);
    g.lineTo(8, 0);
    g.lineTo(0, 10);
    g.lineTo(-8, 0);
    g.closePath();
    g.fill({ color: BEACON_COLOR, alpha: 0.8 });
    g.stroke({ width: 1, color: BEACON_ACCENT, alpha: 0.6 });
  }

  update(delta: number): void {
    const now = Date.now();
    const remaining = this.expiresAt - now;
    const deltaSeconds = delta / 1000;

    // Beacon expired - will be removed by GameScene when server sends remove
    if (remaining <= 0) {
      this.alpha = 0.3;
      return;
    }

    // Fade out in last 3 seconds
    if (remaining < 3000) {
      this.alpha = Math.max(0.2, remaining / 3000);
    }

    this.pulseTime += delta;

    // Spawn periodic waves
    this.waveTimer += delta;
    if (this.waveTimer >= this.WAVE_INTERVAL) {
      this.waveTimer = 0;
      this.waves.push({ radius: 15, maxRadius: this.radius, alpha: 0.4 });
    }

    // Update waves
    this.waves = this.waves.filter(wave => {
      wave.radius += 80 * deltaSeconds;
      wave.alpha = Math.max(0, 0.4 * (1 - wave.radius / wave.maxRadius));
      return wave.radius < wave.maxRadius && wave.alpha > 0.01;
    });

    // Draw range circle + waves
    this.rangeGraphics.clear();
    this.waveGraphics.clear();

    const pulseIntensity = 0.6 + Math.sin(this.pulseTime / 300) * 0.4;

    // Static range boundary (faint pulsing)
    this.rangeGraphics.circle(0, 0, this.radius);
    this.rangeGraphics.stroke({
      width: 1,
      color: BEACON_COLOR,
      alpha: 0.12 * pulseIntensity,
    });

    // Fill the range area very faintly
    this.rangeGraphics.circle(0, 0, this.radius);
    this.rangeGraphics.fill({
      color: BEACON_COLOR,
      alpha: 0.02 * pulseIntensity,
    });

    // Draw expanding wave rings
    for (const wave of this.waves) {
      this.waveGraphics.circle(0, 0, wave.radius);
      this.waveGraphics.stroke({
        width: 1.5,
        color: BEACON_COLOR,
        alpha: wave.alpha * pulseIntensity,
      });
    }

    // Rotate sprite slowly
    if (this.sprite) {
      this.sprite.rotation += 0.001 * delta;
    }
  }
}
